import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';

export default function Login() {
  const { t, login, api, user, loading: appLoading } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (appLoading) return;
    if (user) navigate(user.role === 'student' ? '/profile' : '/dashboard', { replace: true });
  }, [user, appLoading]);
  const [mode, setMode] = useState('student'); // 'student' | 'staff' | 'forgot'
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const handleStudentLogin = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setError(t.invalidCode); return; }
    setLoading(true); setError('');
    try {
      const res = await api('post', '/auth/student-login', { code });
      login(res.data.user, res.data.token);
      navigate('/profile');
    } catch {
      setError(t.invalidCode);
    } finally { setLoading(false); }
  };

  const handleStaffLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await api('post', '/auth/login', { email, password });
      login(res.data.user, res.data.token);
      navigate('/dashboard');
    } catch {
      setError('Invalid credentials. Please try again.');
    } finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await api('post', '/auth/forgot-password', { email });
      setSuccess('If this email exists, a reset link was sent.');
    } catch {
      setError('Something went wrong.');
    } finally { setLoading(false); }
  };

  return (
    <div className="summer-bg" style={{ minHeight: '100vh' }}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            {/* Logo */}
            <div className="text-center mb-4">
              <Link to="/">
                <img src="/logo.png" alt="logo" style={{ height: 80, marginBottom: '1rem' }} />
              </Link>
              <h2 style={{ fontWeight: 900, color: 'var(--navy)' }}>{t.siteName}</h2>
              <div style={{ color: 'var(--muted)', fontWeight: 600 }}>{t.siteSubtitle}</div>
            </div>

            <div className="card-summer p-4">
              {/* Mode tabs */}
              {mode !== 'forgot' && (
                <div className="tab-nav mb-4">
                  <button className={`tab-btn ${mode === 'student' ? 'active' : ''}`} onClick={() => { setMode('student'); setError(''); }}>
                    🎒 {t.studentLogin}
                  </button>
                  <button className={`tab-btn ${mode === 'staff' ? 'active' : ''}`} onClick={() => { setMode('staff'); setError(''); }}>
                    👩‍🏫 {t.teacherLogin}
                  </button>
                </div>
              )}

              {/* Student login */}
              {mode === 'student' && (
                <form onSubmit={handleStudentLogin}>
                  <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <div style={{ fontSize: '4rem' }}>🎒</div>
                    <h4 style={{ fontWeight: 800, marginBottom: '0.5rem' }}>{t.studentLogin}</h4>
                    <p style={{ color: 'var(--muted)', fontSize: '0.9rem', fontWeight: 600 }}>
                      {t.enterCode} 👇
                    </p>
                  </div>
                  <div className="mb-4">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      className="code-input"
                      placeholder="• • • • • •"
                      value={code}
                      onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    />
                  </div>
                  {error && <div className="alert alert-danger mb-3" style={{ borderRadius: 12, fontWeight: 600 }}>{error}</div>}
                  <button type="submit" className="btn-summer w-100" disabled={loading} style={{ fontSize: '1.1rem', padding: '0.9rem' }}>
                    {loading ? '⏳ ...' : `🚀 ${t.loginBtn}`}
                  </button>
                </form>
              )}

              {/* Staff login */}
              {mode === 'staff' && (
                <form onSubmit={handleStaffLogin}>
                  <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <div style={{ fontSize: '4rem' }}>👩‍🏫</div>
                    <h4 style={{ fontWeight: 800 }}>{t.teacherLogin}</h4>
                  </div>
                  <div className="mb-3">
                    <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>
                      {t.enterEmail}
                    </label>
                    <input type="email" className="input-summer" value={email} onChange={e => setEmail(e.target.value)} required />
                  </div>
                  <div className="mb-4">
                    <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>
                      {t.enterPassword}
                    </label>
                    <input type="password" className="input-summer" value={password} onChange={e => setPassword(e.target.value)} required />
                    <div className="text-end mt-2">
                      <button type="button" onClick={() => setMode('forgot')} style={{ background: 'none', border: 'none', color: 'var(--orange)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                        {t.forgotPassword}
                      </button>
                    </div>
                  </div>
                  {error && <div className="alert alert-danger mb-3" style={{ borderRadius: 12, fontWeight: 600 }}>{error}</div>}
                  <button type="submit" className="btn-summer w-100" disabled={loading} style={{ fontSize: '1.1rem', padding: '0.9rem' }}>
                    {loading ? '⏳ ...' : `🚀 ${t.loginBtn}`}
                  </button>
                </form>
              )}

              {/* Forgot password */}
              {mode === 'forgot' && (
                <form onSubmit={handleForgot}>
                  <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <div style={{ fontSize: '3rem' }}>🔑</div>
                    <h4 style={{ fontWeight: 800 }}>{t.forgotPassword}</h4>
                  </div>
                  {success ? (
                    <div className="alert alert-success" style={{ borderRadius: 12, fontWeight: 600 }}>{success}</div>
                  ) : (
                    <>
                      <div className="mb-4">
                        <label style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>
                          {t.enterEmail}
                        </label>
                        <input type="email" className="input-summer" value={email} onChange={e => setEmail(e.target.value)} required />
                      </div>
                      {error && <div className="alert alert-danger mb-3" style={{ borderRadius: 12 }}>{error}</div>}
                      <button type="submit" className="btn-summer w-100" disabled={loading}>
                        {loading ? '⏳ ...' : '📧 Send Reset Link'}
                      </button>
                    </>
                  )}
                  <div className="text-center mt-3">
                    <button type="button" onClick={() => { setMode('staff'); setSuccess(''); setError(''); }}
                      style={{ background: 'none', border: 'none', color: 'var(--orange)', fontWeight: 700, cursor: 'pointer' }}>
                      ← {t.backToLogin}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className="text-center mt-3">
              <Link to="/" style={{ color: 'var(--muted)', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}>
                ← {t.backHome}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
